import { array, hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSData, DSSetup, RecordData, Service } from 'webapp-types';
import {
	BlockVisualization,
	BlockVisualizationSpecial,
	FakeModelIdPrefix,
	ForecastMethod,
	ForecastStatusType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, uniqueId } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import BlockSingleComponent from 'webapp/components/block/single';
import ForecastsDataAddRegressorButtonComponent from 'webapp/components/forecasts/data/add-regressor-button';
import ForecastsDataTableRowForecastCellComponent from 'webapp/components/forecasts/data/table-row-forecast-cell';
import ForecastsDataTableRowMetricCellComponent from 'webapp/components/forecasts/data/table-row-metric-cell';
import ForecastsDataTooltipForecastComponent from 'webapp/components/forecasts/data/tooltip/forecast';
import ForecastsDataTooltipHistoryComponent from 'webapp/components/forecasts/data/tooltip/history';
import MetricDetailsChartLoadingComponent from 'webapp/components/metric-details/chart-loading';
import type { TrackedRequestState } from 'webapp/components/metric-details/forecasts/explorer';
import MetricDetailsTableComponent from 'webapp/components/metric-details/table';
import MetricDetailsTopDatapointsForecastDatapointsComponent from 'webapp/components/metric-details/top-datapoints/forecast-datapoints';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { DataSelect, Forecast } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import BoardState from 'webapp/models-custom/board-state';
import RegressorMetric from 'webapp/models-custom/regressor-metric';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';

// the preview's data selects get fake (uuid) ids before they are pushed to the store
type PreviewDataSelectRaw = Omit<RecordData.DataSelect.Raw, 'id' | 'blockId' | 'updateFrom'> & {
	id: string | number;
	blockId: string | number | null;
	updateFrom?: string | number | null;
};

export interface PreviewData {
	sample: RecordData.Sample.Raw;
	dataSelects: RecordData.DataSelect.Raw[];
	monthsOfUsedHistoricalData: number;
}

interface Args {
	//settings stuff
	forecast: Forecast;
	previewData: PreviewData | null;
	loadingMessages: string[];
	loadingMessageIntervalTime: number;
	mainForecastRequestState: TrackedRequestState;
	isInitialLoading: boolean;
	isRefreshingWithData: boolean;
	currentShownSnapshotId: number | null;
	isViewingHistoricalSnapshot: boolean;
	onRegressorChange: (regressor: RegressorMetric) => void;
	onRequestForecastPreview: () => void;
	executeErrorButtonAction: () => void;
}

interface ForecastsDataIndexComponentInterface<T> {
	Args: T;
	Blocks: {
		settings: [block: Block | null];
	};
}

export default class ForecastsDataIndexComponent extends Component<
	ForecastsDataIndexComponentInterface<Args>
> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/forecasts')
	declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('constants') declare constantsService: Service.Constants;
	@service('store') declare storeService: Service.Store;
	@service('account') declare accountService: Service.Account;

	@tracked boardState: BoardState | null = null;
	@tracked dataSelects: DataSelect[] = [];
	@tracked regressors: RegressorMetric[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.initializeBoardState();
		this.initializeRegressors();
	}

	get isLoading() {
		return this.args.isInitialLoading;
	}

	get isRefreshing() {
		return this.args.isRefreshingWithData;
	}

	get block(): Block | null {
		return this.boardState?.selectedBlock ?? null;
	}

	get blockSetup() {
		return this.block?.blockSetup;
	}

	get visualization() {
		return this.blockSetup?.visualization as VisualizationLineChart | undefined;
	}

	get series() {
		return this.visualization?.series || [];
	}

	get isError() {
		return !!this.args.mainForecastRequestState?.error;
	}

	get isFirstDayOfTheWeekError() {
		const error = this.args.mainForecastRequestState?.error;
		if (!error || !('errors' in error)) {
			return false;
		}
		const errorMessage = error.errors?.[0]?.message;
		return !!errorMessage?.includes('first day of the week');
	}

	get errorButtonText() {
		return this.isFirstDayOfTheWeekError ? 'Change settings' : 'Try again';
	}

	get errorMessage() {
		const error = this.args.mainForecastRequestState?.error;
		if (error instanceof Error && error.message) {
			return error.message;
		}
		return 'Forecast could not be loaded.';
	}

	get hasError() {
		return this.isError;
	}

	get showFooter() {
		return (
			!this.hasError && !this.isLoading && !!this.args.previewData?.monthsOfUsedHistoricalData
		);
	}

	get shouldShowDatapoints() {
		return this.args.previewData && this.boardState && !this.hasError;
	}

	get showDatapointsSection() {
		return this.isLoading || this.shouldShowDatapoints;
	}

	get shouldShowTable() {
		// if main forecast has error but we have regressors, still show the table
		// this is needed so user can fix potential issues with regressors
		if (this.isError && this.hasNoRegressors) {
			return false;
		}

		return !this.hasError || !this.hasNoRegressors;
	}

	get allTableDataSelects(): DataSelect[] {
		const dataSelects: DataSelect[] = [];
		const forecast = this.args.forecast;

		if (forecast.historicalDataSelect) {
			dataSelects.push(forecast.historicalDataSelect);
		}
		if (forecast.forecastDataSelect) {
			dataSelects.push(forecast.forecastDataSelect);
		}

		for (const regressor of this.regressors) {
			if (regressor.historicalDataSelect) {
				dataSelects.push(regressor.historicalDataSelect);
			}
			if (regressor.forecastDataSelect) {
				dataSelects.push(regressor.forecastDataSelect);
			}
		}

		return dataSelects;
	}

	get forecastTimeframeHeader(): string {
		return this.args.forecast.getTimeframeDisplayString();
	}

	get forecastRowLoading(): boolean {
		const mainForecastLoading = !!this.args.mainForecastRequestState?.isLoading;
		const tableBlock = this.args.forecast.tableBlock;
		const tableBlockSyncing = !!tableBlock?.isSyncing;
		return mainForecastLoading || tableBlockSyncing;
	}

	get forecastRowError(): boolean {
		return !!this.args.mainForecastRequestState?.error;
	}

	get isAddRegressorDisabled(): boolean {
		return this.args.isViewingHistoricalSnapshot || this.regressors.length >= 5;
	}

	get hasNoRegressors(): boolean {
		return this.regressors.length === 0;
	}

	// ─── Chart ───────────────────────────────────────────────────────────

	@cached
	get historyPoint() {
		const series = this.series.find((s) => s.isMain);
		if (series) {
			return series.getLastPointWithValue();
		}
		return;
	}

	@cached
	get forecastPoint() {
		const series = this.series.find((s) => s.isForecast);
		if (series) {
			return series.getLastPointWithValue();
		}
		return;
	}

	@action
	initializeRegressors() {
		if (this.args.forecast?.regressors) {
			this.regressors = [...this.args.forecast.regressors];
		} else {
			this.regressors = [];
		}
	}

	@action
	initializeBoardState() {
		const previewData = this.args.previewData;
		if (!previewData) {
			// clean up boardState when previewData is null (starting new forecast)
			if (this.boardState?.selectedBlock) {
				next(() => {
					if (this.boardState?.selectedBlock) {
						const block = this.boardState.selectedBlock;
						this.garbageCollectorService._unloadBlockData(block.id);
						block.model.unloadRecord();
					}
					this.boardState = null;
					this.dataSelects = [];
				});
			}
			return;
		}

		const sample = previewData.sample,
			sampledata = sample?.sampledata,
			dataSelectsData = (previewData.dataSelects || []) as PreviewDataSelectRaw[];

		if (!sampledata) {
			return;
		}

		// if board state already exists, update its data instead of recreating
		if (this.boardState?.selectedBlock) {
			next(() => {
				// clean up old data
				const existingBlock = this.boardState!.selectedBlock!;
				this.garbageCollectorService._unloadBlockData(existingBlock.id);

				const blockId = existingBlock.id!;

				// overwrite dataselect ids & sync with sample
				const dsDataArray = sampledata?.dsData || [],
					primaryDataSelectIdOriginal = dataSelectsData.find((ds) => !ds.type)?.id,
					primaryDataSelectIdNew = this.generateUUID();
				dataSelectsData.forEach((ds) => {
					const isPrimary = ds.id === primaryDataSelectIdOriginal,
						dsId = isPrimary ? primaryDataSelectIdNew : this.generateUUID();
					dsDataArray.some((dsData: DSData.RootTop) => {
						if (dsData.dsId === ds.id) {
							dsData.dsId = dsId;
							return true;
						}
						return false;
					});
					if (!isPrimary) {
						ds.updateFrom = primaryDataSelectIdNew;
					}
					ds.id = dsId;
					ds.blockId = blockId;
				});

				sample.id = blockId;
				sampledata.id = blockId;

				// update existing block properties
				if (sampledata?.dateRange) {
					existingBlock.dateRange = copyObject(sampledata.dateRange);
				}

				// the fake uuid ids aren't in DataSelect.Raw (see PreviewDataSelectRaw)
				this.storeService.pushPayload({
					dataSelects: dataSelectsData as RecordData.DataSelect.Raw[],
					sample,
				});

				this.args.forecast.tableBlock = existingBlock;
			});
			return;
		}

		// initial creation
		next(() => {
			const blockId = this.generateUUID();

			// overwrite dataselect ids & sync with sample
			const dsDataArray = sampledata?.dsData || [],
				primaryDataSelectIdOriginal = dataSelectsData.find((ds) => !ds.type)?.id,
				primaryDataSelectIdNew = this.generateUUID();
			dataSelectsData.forEach((ds) => {
				const isPrimary = ds.id === primaryDataSelectIdOriginal,
					dsId = isPrimary ? primaryDataSelectIdNew : this.generateUUID();
				dsDataArray.some((dsData: DSData.RootTop) => {
					if (dsData.dsId === ds.id) {
						dsData.dsId = dsId;
						return true;
					}
					return false;
				});
				if (!isPrimary) {
					ds.updateFrom = primaryDataSelectIdNew;
				}
				ds.id = dsId;
				ds.blockId = blockId;
			});

			sample.id = blockId;
			sampledata.id = blockId;

			const block = this.mmBlocksService.createNewModel({
				id: blockId,
				dateRange: sampledata?.dateRange
					? copyObject(sampledata.dateRange)
					: ({ range: {}, granulation: {} } as DateRangeInterface.FormulaInterface), // empty formula for forecast blocks
				visualization: BlockVisualization.LINE_CHART,
			});

			// the fake uuid ids aren't in DataSelect.Raw (see PreviewDataSelectRaw)
			this.storeService.pushPayload({
				dataSelects: dataSelectsData as RecordData.DataSelect.Raw[],
				sample,
			});

			// set the fake block on the forecast model so it can access it
			this.args.forecast.tableBlock = block;

			// create board-state
			const boardState = BoardState.createForSingleBlock(block);
			boardState.isViewOnly = true;
			this.boardState = boardState;

			const blockSetup = this.block!.blockSetup;
			blockSetup.isAutosaveOn = false;

			blockSetup.visualization.forceDChartRendererType =
				BlockVisualizationSpecial.FORECAST_CHART;
		});
	}

	@action
	openRegressorMetricSelectionPopup(): void {
		if (this.args.isViewingHistoricalSnapshot) {
			return;
		}

		const initialData: DSSetup.MetricSetupData = {
			key: '',
			spaceAccessId: null,
			dimensions: [],
			filters: null,
		};

		this.mmDataSelectsService
			.openMetricSelectWithAttributes({
				metricSetupData: initialData,
				requireSingleOutput: true,
			})
			.then((metricSetupData: DSSetup.MetricSetupData) => {
				this.addRegressorMetric(metricSetupData);
			})
			.catch(() => {
				// user closed the popup - no action needed
			});
	}

	@action
	executeErrorButtonAction() {
		if (this.args.executeErrorButtonAction) {
			this.args.executeErrorButtonAction();
		}
	}

	@action
	handleRegressorChange(regressor: RegressorMetric): void {
		const regressorIndex = this.regressors.findIndex((r) => r.id === regressor.id);
		if (regressorIndex !== -1) {
			this.regressors = [
				...this.regressors.slice(0, regressorIndex),
				regressor,
				...this.regressors.slice(regressorIndex + 1),
			];
		}

		// persist changes to the forecast model's buffered data
		if (this.args.forecast) {
			this.args.forecast.regressors = this.regressors;
		}

		// set regressor to generating status to show loading indicator
		regressor.setStatusFromResponse({
			type: ForecastStatusType.SNAPSHOT_GENERATING,
			timestamp: Date.now(),
		});

		// delegate to parent handler for preview requests
		this.args.onRegressorChange(regressor);
	}

	@action
	handleRegressorRemove(regressor: RegressorMetric): void {
		const forecast = this.args.forecast;
		if (!forecast) {
			return;
		}

		this.regressors = this.regressors.filter((r) => r.id !== regressor.id);
		forecast.regressors = this.regressors;

		// if this was the last regressor and linear regression is selected, switch to DATABOX
		if (
			this.hasNoRegressors &&
			forecast.methodology?.type === ForecastMethod.LINEAR_REGRESSION
		) {
			forecast.methodology = {
				type: ForecastMethod.DATABOX,
				extra: null,
			};
		}

		this.requestForecastPreviewWithRegressors();
	}

	@action
	handleForecastChange() {
		// reinitialize regressors when forecast model changes (e.g., when snapshot data loads)
		this.initializeRegressors();

		// update tableBlock reference on new forecast model (e.g., after duplication)
		if (this.block && !this.args.forecast.tableBlock) {
			this.args.forecast.tableBlock = this.block;
		}
	}

	override willDestroy() {
		super.willDestroy();

		const block = this.block;
		if (block) {
			this.garbageCollectorService._unloadBlockData(block.id);
			block.model.unloadRecord();
		}
	}

	private generateUUID() {
		return -1 * Number(`${FakeModelIdPrefix.FORECASTS}${uniqueId()}`);
	}

	private addRegressorMetric(metricSetupData: DSSetup.MetricSetupData): void {
		const forecast = this.args.forecast;
		if (!forecast) {
			return;
		}

		const newRegressor = new RegressorMetric({
			forecastId: forecast.id || '',
			metricSetupKey: metricSetupData.key,
			metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
			metricSetupDimensions: metricSetupData.dimensions || [],
			metricSetupFilters: metricSetupData.filters || null,
			correlation: null,
			methodology: {
				type: ForecastMethod.DATABOX,
				extra: null,
			},
		});

		this.regressors = [...this.regressors, newRegressor];
		forecast.regressors = this.regressors;

		const metric = newRegressor.metricSetup?.metric;
		if (metric) {
			this.analyticsService.event(TrackingEvent.FORECASTING_IMPACTING_METRIC_ADDED, {
				datasource: metric.dataSourceTypeKey,
				metric: metric.name,
			});
		}

		newRegressor.isCalculatingCorrelation = true;
		newRegressor.setStatusFromResponse({
			type: ForecastStatusType.SNAPSHOT_GENERATING,
			timestamp: Date.now(),
		});

		const forecastMetricSetupData = forecast.metricSetup.metricSetupData;
		const regressorMetricSetupData = newRegressor.metricSetup?.metricSetupData;

		// regressor preview is now bundled in the main forecast request
		// set loading state and let the main forecast response update it

		if (!forecastMetricSetupData || !regressorMetricSetupData) {
			newRegressor.setCalculatedCorrelation();
			newRegressor.isCalculatingCorrelation = false;

			// trigger reactivity by recreating the array
			this.regressors = [...this.regressors];

			// request main forecast preview with updated correlation
			this.requestForecastPreviewWithRegressors();
		} else {
			this.mmForecastsService
				.requestMetricCorrelation(forecastMetricSetupData, regressorMetricSetupData)
				.then((correlationResponse) => {
					newRegressor.setCalculatedCorrelation(correlationResponse.correlation);
				})
				.catch((_error) => {
					newRegressor.setCalculatedCorrelation();
				})
				.finally(() => {
					newRegressor.isCalculatingCorrelation = false;

					// trigger reactivity by recreating the array
					this.regressors = [...this.regressors];

					// request main forecast preview with updated correlation
					this.requestForecastPreviewWithRegressors();
				});
		}
	}

	private requestForecastPreviewWithRegressors(): void {
		this.args.onRequestForecastPreview();
	}
	<template>
		<div
			class="forecast-content-container"
			{{didUpdate this.initializeBoardState @previewData}}
			{{didUpdate this.handleForecastChange @forecast}}
		>
			<div class="metric-details__data-grid">
				{{! Left column: Datapoints + Chart }}
				<div class="main-content-column metric-details__chart-column">
					{{#if this.showDatapointsSection}}
						<MetricDetailsTopDatapointsForecastDatapointsComponent
							@forecast={{@forecast}}
							@isLoading={{this.isLoading}}
							@isRefreshing={{this.isRefreshing}}
						/>
					{{/if}}

					<div class="main-chart-content">
						{{#if this.isLoading}}
							<MetricDetailsChartLoadingComponent
								@messages={{@loadingMessages}}
								@intervalTime={{@loadingMessageIntervalTime}}
							/>
						{{else if this.isError}}
							<div class="status status--error">
								<img alt="Forecast" src="/images/forecast_graph_error.png" />
								<span class="title">Could not load Metric forecasts</span>
								<span class="description">
									{{#if this.isFirstDayOfTheWeekError}}
										For the forecast functionality to work with weekly
										granulation,<br />
										it is necessary to set Monday as the first day of the week
										for the account.
										{{#unless
											this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
										}}
											<a
												role="button"
												class
												href="https://help.databox.com/databox-forecasting-methodology"
												target="_blank"
											>
												Learn more
											</a>
										{{/unless}}
									{{else}}
										{{this.errorMessage}}
									{{/if}}
								</span>
								<div class="button-group">
									<SubmitButtonComponent
										@text={{this.errorButtonText}}
										@icon={{unless this.isFirstDayOfTheWeekError "Refresh"}}
										@onTrigger={{this.executeErrorButtonAction}}
										@noDelay={{true}}
									/>
								</div>
							</div>
						{{else if this.boardState}}
							<div
								class="block-single-wrapper
									{{if this.isRefreshing 'forecast-content-refreshing'}}"
							>
								<BlockSingleComponent
									@boardState={{this.boardState}}
									@skipBlockFetchLoader={{true}}
								>
									{{#if this.historyPoint}}
										<ForecastsDataTooltipHistoryComponent
											@model={{this.historyPoint}}
										/>
									{{/if}}
									{{#if this.forecastPoint}}
										<ForecastsDataTooltipForecastComponent
											@model={{this.forecastPoint}}
										/>
									{{/if}}
								</BlockSingleComponent>
							</div>
						{{/if}}
					</div>

					{{! Historical data info }}
					{{#if this.showFooter}}
						<div class="forecast-chart-actions">
							<div class="forecast-chart-actions__left">
								<span class="forecast-historical-data-info">
									Forecast based on the last
									<strong>
										{{@previewData.monthsOfUsedHistoricalData}}
										months
									</strong>
									of historical data.
								</span>
							</div>
						</div>
					{{/if}}
				</div>

				{{! Right column: Settings panel }}
				<div class="forecast_settings">
					{{yield this.block to="settings"}}
				</div>
			</div>

			{{#if this.shouldShowTable}}
				{{! Table — shows skeletons during loading }}
				<MetricDetailsTableComponent
					@dataSelects={{this.allTableDataSelects}}
					@labelColumnTitle="Forecasted & impacting metrics"
					@summaryColumnTitle={{this.forecastTimeframeHeader}}
				>
					<:body as |table|>
						{{! Main forecast row }}
						<table.Row
							@dataSelect={{@forecast.historicalDataSelect}}
							@additionalValues={{array
								(hash dataSelect=@forecast.forecastDataSelect)
							}}
							@indicatorConfig={{hash
								dataSelect=@forecast.forecastDataSelect
								changeInvert=@forecast.metricSetup.metric.changeInvert
							}}
							@cellClass="forecast-cell"
							@isLoading={{this.forecastRowLoading}}
							@isError={{this.forecastRowError}}
						>
							<:header>
								<ForecastsDataTableRowMetricCellComponent
									@model={{@forecast}}
									@forecast={{@forecast}}
									@mainForecastRequestState={{@mainForecastRequestState}}
									@isViewingHistoricalSnapshot={{@isViewingHistoricalSnapshot}}
									@onRegressorChange={{this.handleRegressorChange}}
									@onRegressorRemove={{this.handleRegressorRemove}}
								/>
							</:header>
							<:summary>
								<ForecastsDataTableRowForecastCellComponent
									@model={{@forecast}}
									@mainForecastRequestState={{@mainForecastRequestState}}
								/>
							</:summary>
						</table.Row>

						{{! Regressor rows }}
						{{#each this.regressors as |regressor|}}
							<table.Row
								@dataSelect={{regressor.historicalDataSelect}}
								@additionalValues={{array
									(hash dataSelect=regressor.forecastDataSelect)
								}}
								@indicatorConfig={{hash
									dataSelect=regressor.forecastDataSelect
									changeInvert=@forecast.metricSetup.metric.changeInvert
								}}
								@isLoading={{regressor.isLoading}}
								@isError={{regressor.hasMetricUnavailableError}}
							>
								<:header>
									<ForecastsDataTableRowMetricCellComponent
										@model={{regressor}}
										@forecast={{@forecast}}
										@mainForecastRequestState={{@mainForecastRequestState}}
										@isViewingHistoricalSnapshot={{@isViewingHistoricalSnapshot}}
										@onRegressorChange={{this.handleRegressorChange}}
										@onRegressorRemove={{this.handleRegressorRemove}}
									/>
								</:header>
								<:summary>
									<ForecastsDataTableRowForecastCellComponent
										@model={{regressor}}
										@mainForecastRequestState={{@mainForecastRequestState}}
									/>
								</:summary>
							</table.Row>
						{{/each}}
					</:body>
					<:footer>
						<ForecastsDataAddRegressorButtonComponent
							@onRegressorAdd={{this.openRegressorMetricSelectionPopup}}
							@showExplanation={{this.hasNoRegressors}}
							@isDisabled={{this.isAddRegressorDisabled}}
						/>
					</:footer>
				</MetricDetailsTableComponent>
			{{/if}}
		</div>
	</template>
}
